import React, { useState } from "react";
import { Heart, Repeat, Gift } from "lucide-react";
import { api, formatZAR } from "../lib/api";
import { submitPayFast } from "../lib/payfast";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const PRESETS = [50, 100, 200, 500, 1000];

export default function Donate() {
  const { user } = useAuth();
  const [amount, setAmount] = useState(200);
  const [custom, setCustom] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [donorName, setDonorName] = useState(user?.name || "");
  const [donorEmail, setDonorEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);

  const finalAmount = custom ? parseFloat(custom) : amount;

  const donate = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < 10) {
      toast.error("Minimum donation is R10");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/payfast/donation", {
        amount: finalAmount,
        recurring,
        donor_name: donorName || "Anonymous",
        donor_email: donorEmail || "",
        origin_url: window.location.origin,
      });
      submitPayFast(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div>
      <section className="bg-[#0B1526] text-white py-24 sm:py-32 relative overflow-hidden" data-testid="donate-hero">
        <img src="https://images.unsplash.com/photo-1772724317387-7e763804b637?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-12 h-12 text-[#C00000] mx-auto mb-6" />
          <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-5">Give</div>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] mb-6">
            Fund the <span className="italic text-[#C00000]">movement.</span>
          </h1>
          <p className="text-white/70 max-w-xl mx-auto text-lg leading-relaxed">
            Your giving funds free community events, artist development scholarships, and the
            production of worship content that ministers to thousands every month.
          </p>
        </div>
      </section>

      <section className="py-20 sm:py-24" data-testid="donate-form-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          <form onSubmit={donate} className="lg:col-span-3 bg-white border border-gray-200 p-8 sm:p-10 space-y-7" data-testid="form-donation">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-4">Choose an amount</div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
                {PRESETS.map((v) => (
                  <button
                    type="button"
                    key={v}
                    onClick={() => { setAmount(v); setCustom(""); }}
                    className={`py-3 text-sm border transition-colors ${
                      amount === v && !custom ? "bg-[#1F3864] text-white border-[#1F3864]" : "border-gray-300 hover:border-[#1F3864]"
                    }`}
                    data-testid={`action-amount-${v}`}
                  >
                    {formatZAR(v)}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Custom amount (ZAR):</span>
                <input
                  type="number"
                  min="10"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="Enter"
                  className="flex-1 px-3 py-2 border border-gray-300 focus:border-[#1F3864] outline-none text-sm"
                  data-testid="input-custom-donation-amount"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-recurring-donation">
                <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} className="w-5 h-5 accent-[#C00000]" />
                <div className="flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-[#1F3864]" />
                  <span className="text-sm">Make this a monthly recurring donation</span>
                </div>
              </label>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <input value={donorName} onChange={(e) => setDonorName(e.target.value)} placeholder="Your name (optional)" className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-donor-name" />
              <input type="email" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} placeholder="Email for receipt" className="w-full px-4 py-3 border border-gray-300 focus:border-[#1F3864] outline-none text-sm" data-testid="input-donor-email" />
            </div>

            <div className="p-5 bg-[#F5F5F5]">
              <div className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">You are giving</div>
              <div className="font-display text-4xl text-[#1F3864]">{formatZAR(finalAmount)} {recurring && <span className="text-base text-gray-500">/ month</span>}</div>
            </div>

            <button type="submit" disabled={busy || !finalAmount} className="btn-primary-red w-full justify-center !py-4" data-testid="action-donate-submit-button">
              <Gift className="w-4 h-4" /> {busy ? "Redirecting..." : `Give ${formatZAR(finalAmount)}${recurring ? "/month" : ""}`}
            </button>
            <p className="text-xs text-gray-500 text-center">
              Secure payment processed by Stripe. You'll receive an automatic email receipt for your records.
            </p>
          </form>

          <aside className="lg:col-span-2 space-y-6">
            <div className="bg-[#1F3864] text-white p-8">
              <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-3">Where it goes</div>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3"><span className="text-[#C00000] font-display text-2xl leading-none">40%</span> Artist Development scholarships</li>
                <li className="flex items-start gap-3"><span className="text-[#C00000] font-display text-2xl leading-none">30%</span> Free community worship events</li>
                <li className="flex items-start gap-3"><span className="text-[#C00000] font-display text-2xl leading-none">20%</span> Content production & equipment</li>
                <li className="flex items-start gap-3"><span className="text-[#C00000] font-display text-2xl leading-none">10%</span> Operations & administration</li>
              </ul>
            </div>
            <div className="border border-gray-200 p-6">
              <div className="text-xs tracking-[0.2em] uppercase text-[#C00000] mb-2">Tax Receipt</div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Next Gen Praise NPC is a registered non-profit. Once our PBO status is confirmed, your
                donation will qualify for a Section 18A certificate.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
